import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2, ArrowLeft } from "lucide-react";
import {
  getCustomers, getProducts, getSettings,
  createQuote, updateQuote, getQuote,
  createInvoice, updateInvoice, getInvoice,
} from "@/lib/api";
import { fmtEur, computeTotals, todayISO, addDaysISO } from "@/lib/format";
import { useI18n } from "@/i18n";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const DocumentForm = ({ kind }) => {
  const { t } = useI18n();
  const { id } = useParams();
  const navigate = useNavigate();
  const isQuote = kind === "quote";
  const editing = !!id;

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [issueDate, setIssueDate] = useState(todayISO());
  const [serviceDate, setServiceDate] = useState("");
  const [secondDate, setSecondDate] = useState(addDaysISO(isQuote ? 30 : 14));
  const [notes, setNotes] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [items, setItems] = useState([]);
  const [defaultTax, setDefaultTax] = useState(19);

  useEffect(() => {
    getCustomers().then(setCustomers);
    getProducts().then(setProducts);
    getSettings().then((s) => setDefaultTax(s.default_tax_rate ?? 19));
    if (editing) {
      (isQuote ? getQuote(id) : getInvoice(id)).then((d) => {
        setCustomerId(d.customer_id);
        setIssueDate(d.issue_date);
        setServiceDate(d.service_date || "");
        setSecondDate(isQuote ? d.valid_until : d.due_date);
        setNotes(d.notes || "");
        setPaymentTerms(d.payment_terms || "");
        setItems(d.items || []);
      });
    }
  }, [id]);

  const addItem = () => setItems([...items, { description: "", quantity: 1, unit_price: 0, tax_rate: defaultTax }]);
  const addProduct = (pid) => {
    const p = products.find((x) => x.id === pid);
    if (!p) return;
    setItems([...items, { product_id: p.id, description: p.name, quantity: 1, unit_price: p.unit_price, tax_rate: p.tax_rate }]);
  };
  const updateItem = (idx, key, val) => {
    const next = [...items];
    next[idx] = { ...next[idx], [key]: key === "description" ? val : val };
    setItems(next);
  };
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const totals = computeTotals(items);

  const submit = async () => {
    if (!customerId) { toast.error(t("doc.selectCustomer")); return; }
    if (items.length === 0) { toast.error(t("doc.noItems")); return; }
    const payload = {
      customer_id: customerId,
      issue_date: issueDate,
      notes,
      payment_terms: paymentTerms,
      items: items.map((it) => ({
        product_id: it.product_id || null,
        description: it.description,
        quantity: Number(it.quantity) || 0,
        unit_price: Number(it.unit_price) || 0,
        tax_rate: Number(it.tax_rate) || 0,
      })),
    };
    if (isQuote) payload.valid_until = secondDate;
    else { payload.due_date = secondDate; payload.service_date = serviceDate || null; }

    let res;
    if (editing) res = await (isQuote ? updateQuote(id, payload) : updateInvoice(id, payload));
    else res = await (isQuote ? createQuote(payload) : createInvoice(payload));
    toast.success(editing ? t("common.save") : t("common.create"));
    navigate(`/${isQuote ? "quotes" : "invoices"}/${res.id}`);
  };

  const title = editing ? t(isQuote ? "doc.quoteEdit" : "doc.invoiceEdit") : t(isQuote ? "doc.quoteNew" : "doc.invoiceNew");

  return (
    <div className="mx-auto max-w-4xl">
      <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary" data-testid="back-button">
        <ArrowLeft className="h-4 w-4" /> {t("common.back")}
      </button>
      <PageHeader title={title} />

      <Card className="p-6">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="grid gap-1.5 sm:col-span-1">
            <Label className="text-xs font-medium text-slate-600">{t("doc.customer")}</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger data-testid="customer-select"><SelectValue placeholder={t("doc.selectCustomer")} /></SelectTrigger>
              <SelectContent>
                {customers.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`customer-opt-${c.id}`}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs font-medium text-slate-600">{t("doc.issueDate")}</Label>
            <Input data-testid="issue-date-input" type="date" value={issueDate || ""} onChange={(e) => setIssueDate(e.target.value)} />
          </div>
          {!isQuote && (
            <div className="grid gap-1.5">
              <Label className="text-xs font-medium text-slate-600">{t("doc.serviceDate")}</Label>
              <Input data-testid="service-date-input" type="date" value={serviceDate || ""} onChange={(e) => setServiceDate(e.target.value)} />
            </div>
          )}
          <div className="grid gap-1.5">
            <Label className="text-xs font-medium text-slate-600">{isQuote ? t("doc.validUntil") : t("doc.dueDate")}</Label>
            <Input data-testid="second-date-input" type="date" value={secondDate || ""} onChange={(e) => setSecondDate(e.target.value)} />
          </div>
        </div>

        <div className="mt-8">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-display text-base font-semibold text-slate-900">{t("doc.items")}</h3>
            <div className="flex gap-2">
              {products.length > 0 && (
                <Select onValueChange={addProduct} value="">
                  <SelectTrigger data-testid="add-product-select" className="h-9 w-48"><SelectValue placeholder={t("doc.addProduct")} /></SelectTrigger>
                  <SelectContent>
                    {products.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`add-product-opt-${p.id}`}>{p.name} · {fmtEur(p.unit_price)}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-item-button" className="h-9"><Plus className="mr-1 h-4 w-4" /> {t("doc.addItem")}</Button>
            </div>
          </div>

          <div className="space-y-2">
            <div className="hidden grid-cols-[1fr_80px_110px_80px_110px_36px] gap-2 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:grid">
              <span>{t("doc.description")}</span>
              <span className="text-right">{t("doc.qty")}</span>
              <span className="text-right">{t("doc.unitPrice")}</span>
              <span className="text-right">{t("doc.taxRate")}</span>
              <span className="text-right">{t("doc.lineTotal")}</span>
              <span />
            </div>
            {items.map((it, idx) => {
              const line = Number(it.quantity || 0) * Number(it.unit_price || 0);
              return (
                <div key={idx} data-testid={`item-row-${idx}`} className="grid grid-cols-2 gap-2 rounded-lg border border-border p-2 sm:grid-cols-[1fr_80px_110px_80px_110px_36px] sm:items-center sm:border-0 sm:p-0">
                  <Input data-testid={`item-desc-${idx}`} className="col-span-2 sm:col-span-1" placeholder={t("doc.description")} value={it.description} onChange={(e) => updateItem(idx, "description", e.target.value)} />
                  <Input data-testid={`item-qty-${idx}`} type="number" step="0.01" className="text-right" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} />
                  <Input data-testid={`item-price-${idx}`} type="number" step="0.01" className="text-right" value={it.unit_price} onChange={(e) => updateItem(idx, "unit_price", e.target.value)} />
                  <Input data-testid={`item-tax-${idx}`} type="number" step="0.1" className="text-right" value={it.tax_rate} onChange={(e) => updateItem(idx, "tax_rate", e.target.value)} />
                  <div className="flex items-center justify-end font-mono-num text-sm font-semibold text-slate-800">{fmtEur(line)}</div>
                  <button data-testid={`remove-item-${idx}`} onClick={() => removeItem(idx)} className="flex items-center justify-end text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              );
            })}
            {items.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">{t("doc.noItems")}</p>}
          </div>

          <div className="mt-6 flex justify-end">
            <div className="w-full max-w-xs space-y-2 text-sm">
              <div className="flex justify-between text-slate-600"><span>{t("doc.subtotal")}</span><span className="font-mono-num" data-testid="form-subtotal">{fmtEur(totals.subtotal)}</span></div>
              <div className="flex justify-between text-slate-600"><span>{t("doc.tax")}</span><span className="font-mono-num">{fmtEur(totals.tax_total)}</span></div>
              <div className="flex justify-between border-t border-slate-300 pt-2 text-base font-bold text-slate-900"><span>{t("common.total")}</span><span className="font-mono-num text-primary" data-testid="form-total">{fmtEur(totals.total)}</span></div>
            </div>
          </div>
        </div>

        <div className="mt-6 grid gap-1.5">
          <Label className="text-xs font-medium text-slate-600">{t("doc.notes")}</Label>
          <Textarea data-testid="notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
        </div>

        <div className="mt-6 grid gap-1.5">
          <Label className="text-xs font-medium text-slate-600">{t("doc.paymentTerms")}</Label>
          <Textarea data-testid="payment-terms-input" value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} rows={2} />
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={() => navigate(-1)}>{t("common.cancel")}</Button>
          <Button onClick={submit} data-testid="save-document-button">{t("common.save")}</Button>
        </div>
      </Card>
    </div>
  );
};

export default DocumentForm;
